import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash-es/isEmpty';
import ErrorTable from './ErrorTable';
import Timer from '../../components/Timer';
import { parseErrors } from '../../utilities/parser';
import { formatDate } from '../../utilities/timeUtility';
import Translation from '../../utilities/localizationUtility';
import { demoData } from '../../demoData/reservation';
import { WidgetSetting } from '../../types';
import Spinner from '../../components/Spinner';
import BrandingString from '../components/BrandingString';

export default class ReservationPage extends Component {
  constructor(props) {
    super(props);

    this.wrapperRef = React.createRef();

    this.state = {
      isShowError: false,
      isAnyErrorItem: false,
      formError: '',
      items: [],
      expiresAt: '',
      leftSeconds: 0,
      isLoading: false,
      currentCartToken: null,
    };

    this.handleOnTrigger = this.handleOnTrigger.bind(this);
    this.onExpired = this.onExpired.bind(this);
    this.onXChildData = this.onXChildData.bind(this);
    this.setSpinnerRef = this.setSpinnerRef.bind(this);
    this.onRetry = this.onRetry.bind(this);
  }

  componentDidMount() {
    if (window.xprops) {
      window.xprops.onProps(this.onXChildData);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { onTrigger, setting } = this.props;
    const { expiresAt, items, leftSeconds } = this.state;
    const { isShowError: prevIsShowError } = prevState;

    if (!+setting.when_expired && items.length && expiresAt && leftSeconds === 0) {
      onTrigger('removeItems', items.map((x) => x.deposit_external_id || x.external_id));
    }

    if (onTrigger) {
      const defaultHeight = 60;
      const wrapper = this.wrapperRef.current;
      const wrapperHeight = wrapper ? wrapper.clientHeight : defaultHeight;
      onTrigger('resize', prevIsShowError ? defaultHeight : wrapperHeight);
    }
  }

  handleOnTrigger() {
    const { onTrigger } = this.props;
    onTrigger('hide');
  }

  onNewItems({
    items, checkout = false, expiresAt = '', leftSeconds = 0,
  }) {
    const isAnyErrorItem = items.some((item) => item.status !== 'ok');
    const { onTrigger, setting: { position } } = this.props;
    const hideWidget = !isAnyErrorItem && (position === 'None');

    if (checkout && !isAnyErrorItem) {
      onTrigger('checkout');
    } else {
      onTrigger('enableCheckout', !isAnyErrorItem);
      const trigger = items.length && !hideWidget ? 'show' : 'hide';
      this.setState({
        items, isAnyErrorItem, expiresAt, leftSeconds, isLoading: false,
      });
      onTrigger(trigger);
    }
  }

  onXChildData(input) {
    let data;
    const { isDemo } = this.props;
    const { currentCartToken, expiresAt: currentExpiresAt } = this.state;

    try {
      if (input) {
        data = input.data;
      }

      if (!data) {
        return;
      }

      if (isDemo) {
        this.onNewItems(demoData());
        return;
      }

      if (!data.items) {
        return;
      }

      const { checkout, items, checkExpires } = data;
      const serverErrorStatuses = new Array(527 - 500).fill(undefined).map((_, i) => i + 500);
      const { reservationActions: { submitReservation }, onTrigger } = this.props;

      data.checkExpires = checkExpires ? currentExpiresAt : null;

      submitReservation(data)
        .then(({
          data: {
            reservation: {
              cart_token: cartToken, expires_at: expiresAt, items: newItems, left_seconds: leftSeconds,
            },
          },
        }) => {
          const newCartToken = newItems.length ? cartToken : null;
          if (currentCartToken !== newCartToken) {
            onTrigger('updateCartToken', newCartToken);
            this.setState({ currentCartToken: newCartToken });
          }
          this.onNewItems({
            items: newItems,
            checkout,
            expiresAt,
            leftSeconds,
          });
        })
        .catch((responsed) => {
          const { response } = responsed;
          this.setState({ formError: '' });
          if (!response) {
            console.error(`[BTA: reservation widget] Network error during reservation check — checkout may remain blocked. Contact BookThatApp Support for assistance.`);
            // here is a trick to make promise error to trigger window.onerror
            setTimeout(() => {
              throw responsed;
            });
          } else if (response.status === 401) {
            this.onNewItems({ items: [], checkout });
          } else if (response.status === 410) {
            onTrigger('checkout');
          } else if (serverErrorStatuses.includes(response.status)) { // Codes 5xx
            console.error(`[BTA: reservation widget] Server error ${response.status} (${response.statusText}) during reservation check — checkout may remain blocked. Contact BookThatApp Support for assistance.`);
          } else if (response.data) {
            if (response.data.form) {
              this.setState({ formError: response.data.form });
            } else {
              const { error_items: errorItems, errors, variants } = response.data;
              const newResponse = { data: { items: errorItems, errors, variants } };
              this.onNewItems({ items: parseErrors(items, newResponse) });
            }
          } else {
            console.error(`[BTA: reservation widget] Unexpected error ${response.status} (${response.statusText}) during reservation check — checkout may remain blocked. Contact BookThatApp Support for assistance.`);
          }
        });
    } catch (e) {
      // here is a trick to make promise error to trigger window.onerror
      setTimeout(() => {
        throw e;
      });
    }
  }

  onExpired() {
    const { onTrigger, setting } = this.props;

    if (!+setting.when_expired) {
      const { items } = this.state;
      onTrigger('removeItems', items.map((x) => x.deposit_external_id || x.external_id));
    } else {
      // trigger re-render
      onTrigger('enableCheckout', false);
      this.setState({ leftSeconds: 0 });
    }
  }

  onShowError() {
    const { isShowError } = this.state;
    this.setState({ isShowError: !isShowError });
  }

  setSpinnerRef(ref) {
    this.spinner = ref;
  }

  renderErrorItem(items) {
    const { isShowError } = this.state;
    const t = new Translation(this.props);
    const { style } = this.props;

    return (
      <div style={window.screen.width > 576 ? { padding: '0 15px', flexGrow: 1 } : { flexGrow: 1 }}>
        {isShowError && <ErrorTable style={style} show={isShowError} items={items} translation={t} />}

        <div className="flexbox vertical-center space-between">
          <p style={this.textStyle({})}>{t.key('bookingNotReserved')}</p>
          <input
            type="button"
            style={this.buttonStyle({ margin: '0 1em' })}
            onClick={this.onShowError.bind(this)}
            className="pure-button fz14"
            value={t.key('updateReservation')}
          />
        </div>
      </div>
    );
  }

  buttonStyle(styles) {
    const { style, defaultSetting } = this.props;
    const theme = defaultSetting.theme || {};
    return {
      ...styles, ...style.body, color: theme.buttonTextColor, background: theme.buttonColor,
    };
  }

  textStyle(styles) {
    const { style, defaultSetting } = this.props;
    const theme = defaultSetting.theme || {};
    return { ...styles, ...style.body, color: theme.textColor };
  }

  layoutStyle(styles) {
    const { style, defaultSetting } = this.props;
    const theme = defaultSetting.theme || {};
    return {
      ...styles,
      ...style.body,
      ...style.boxShadow,
      color: theme.textColor,
      background: theme.reservationBannerBackgroundColor || theme.backgroundColor,
    };
  }

  onRetry() {
    const { spinner } = this;
    const { onTrigger } = this.props;
    this.setState({ isLoading: true });

    if (spinner) {
      spinner.show();
    }

    onTrigger('refresh');
  }

  renderRetryButton(value) {
    const { isLoading } = this.state;
    return (
      <>
        <Spinner ref={this.setSpinnerRef} />
        <input
          type="button"
          style={{ margin: '0 1em' }}
          onClick={this.onRetry}
          className="expired pure-button fz14"
          value={value || 'Retry'}
          disabled={isLoading}
        />
      </>
    );
  }

  renderExpiredMessage() {
    const { setting, onTrigger } = this.props;
    const textStyle = this.textStyle({ margin: '1em' });
    const t = new Translation(this.props);

    if (onTrigger) {
      onTrigger('enableCheckout', false);
    }

    return (
      <div className="flexbox space-between vertical-center">
        <p style={textStyle}>{t.key('reservationExpired')}</p>
        {!isEmpty(setting) && !!+setting.when_expired
          && this.renderRetryButton(t.key('retryButtonLabel'))}
      </div>
    );
  }

  render() {
    const { setting: { position }, timeZone, brandingVisualized } = this.props;
    const { formError, isAnyErrorItem, items, expiresAt, leftSeconds } = this.state;
    const textStyle = this.textStyle({ margin: '1em' });
    const layoutStyle = this.layoutStyle({ padding: '5px 15px', opacity: 0.98 });
    const t = new Translation(this.props);

    if (['Bottom', 'None'].includes(position) || !position) {
      layoutStyle.transform = 'rotate(180deg)';
    }

    if (items.length > 0) {
      return (
        <div className="flexbox space-between vertical-center" style={layoutStyle} ref={this.wrapperRef}>
          {
            isAnyErrorItem
              ? this.renderErrorItem(items)
              : (
                <div style={{ flexGrow: 1 }}>
                  {expiresAt && leftSeconds === 0 ? (
                    this.renderExpiredMessage()
                  ) : (
                    <div className="flexbox space-between vertical-center">
                      {formError ? (
                          <p style={textStyle}>
                            {t.key(formError)}
                          </p>
                      )
                        : (
                        <>
                          <p style={textStyle}>
                            {`${t.key('success')} ${formatDate(expiresAt, timeZone)}`}
                          </p>
                          <div style={({ ...textStyle, flexBasis: '80px' })}>
                            <Timer seconds={leftSeconds} onExpired={this.onExpired} />
                          </div>
                        </>
                        )}
                    </div>
                  )}
                </div>
              )
          }
          <input
            type="button"
            style={this.buttonStyle({ fontSize: '24px', padding: '0 8px 2px' })}
            className="pure-button fz14"
            onClick={this.handleOnTrigger}
            value="&times;"
          />
          {brandingVisualized && <BrandingString />}
        </div>
      );
    }

    return null;
  }
}

ReservationPage.propTypes = {
  timeZone: PropTypes.string.isRequired,
  setting: WidgetSetting,
  onTrigger: PropTypes.func,
  reservationActions: PropTypes.shape({ submitReservation: PropTypes.func }).isRequired,
  style: PropTypes.object.isRequired,
};
